// These are the rules by which the form input fields will be checked against.

import { formHasErrors, setFormValues } from "./objectValidation";

// Validation constants - easily configurable limits
const KEYWORD_LIMIT_MIN = 1;
const KEYWORD_LIMIT_MAX = 100;
const KEYWORD_THRESHOLD_MIN = 0;
const KEYWORD_THRESHOLD_MAX = 1;

const SEMANTIC_LIMIT_MIN = 1;
const SEMANTIC_LIMIT_MAX = 100;
const SEMANTIC_THRESHOLD_MIN = 0;
const SEMANTIC_THRESHOLD_MAX = 1;
const SEMANTIC_MAX_RESULTS_PER_RESOURCE_MIN = 1;
const SEMANTIC_MAX_RESULTS_PER_RESOURCE_MAX = 50;
const SEMANTIC_MIN_DURATION_MIN = 0;
const SEMANTIC_MIN_DURATION_MAX = 3600; // 1 hour in seconds

const ALLOWED_KEYWORD_TABLES = ["ALL", "RESOURCES", "TRANSCRIPTS", "QUOTES", "AUTHORS"];

/**
 * Criteria:
 * 1) Must be in ALLOWED_KEYWORD_TABLES
 */
const keywordTableValidation = (prevConfig, row, value) => {
    // 1)
    if (!ALLOWED_KEYWORD_TABLES.includes(value)) {
        return setFormValues(row, "keywordTable", true, `Must be one of: ${ALLOWED_KEYWORD_TABLES.join(", ")}`);
    }

    return setFormValues(row, "keywordTable", false, "");
};

/**
 * Criteria:
 * 1) Required field
 * 2) Must be a positive integer
 * 3) Must be at least KEYWORD_LIMIT_MIN
 * 4) Must be at most KEYWORD_LIMIT_MAX
 */
const keywordLimitValidation = (prevConfig, row, value) => {
    // 1)
    if (value === "") {
        return setFormValues(row, "keywordLimit", true, "Required");
    }

    // 2)
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue !== parseFloat(value)) {
        return setFormValues(row, "keywordLimit", true, "Must be a whole number");
    }

    // 3)
    if (numValue < KEYWORD_LIMIT_MIN) {
        return setFormValues(row, "keywordLimit", true, `Must be at least ${KEYWORD_LIMIT_MIN}`);
    }

    // 4)
    if (numValue > KEYWORD_LIMIT_MAX) {
        return setFormValues(row, "keywordLimit", true, `Must be at most ${KEYWORD_LIMIT_MAX}`);
    }

    return setFormValues(row, "keywordLimit", false, "");
};

/**
 * Criteria:
 * 1) Required field
 * 2) Must be a number between KEYWORD_THRESHOLD_MIN and KEYWORD_THRESHOLD_MAX
 */
const keywordThresholdValidation = (prevConfig, row, value) => {
    // 1)
    if (value === "") {
        return setFormValues(row, "keywordThreshold", true, "Required");
    }

    // 2)
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
        return setFormValues(row, "keywordThreshold", true, "Must be a number");
    }

    if (numValue < KEYWORD_THRESHOLD_MIN || numValue > KEYWORD_THRESHOLD_MAX) {
        return setFormValues(row, "keywordThreshold", true, `Must be between ${KEYWORD_THRESHOLD_MIN} and ${KEYWORD_THRESHOLD_MAX}`);
    }

    return setFormValues(row, "keywordThreshold", false, "");
};

/**
 * Criteria:
 * 1) Required field
 * 2) Must be a positive integer
 * 3) Must be at least SEMANTIC_LIMIT_MIN
 * 4) Must be at most SEMANTIC_LIMIT_MAX
 */
const semanticLimitValidation = (prevConfig, row, value) => {
    // 1)
    if (value === "") {
        return setFormValues(row, "semanticLimit", true, "Required");
    }

    // 2)
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue !== parseFloat(value)) {
        return setFormValues(row, "semanticLimit", true, "Must be a whole number");
    }

    // 3)
    if (numValue < SEMANTIC_LIMIT_MIN) {
        return setFormValues(row, "semanticLimit", true, `Must be at least ${SEMANTIC_LIMIT_MIN}`);
    }

    // 4)
    if (numValue > SEMANTIC_LIMIT_MAX) {
        return setFormValues(row, "semanticLimit", true, `Must be at most ${SEMANTIC_LIMIT_MAX}`);
    }

    return setFormValues(row, "semanticLimit", false, "");
};

/**
 * Criteria:
 * 1) Required field
 * 2) Must be a number between SEMANTIC_THRESHOLD_MIN and SEMANTIC_THRESHOLD_MAX
 */
const semanticThresholdValidation = (prevConfig, row, value) => {
    // 1)
    if (value === "") {
        return setFormValues(row, "semanticThreshold", true, "Required");
    }

    // 2)
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
        return setFormValues(row, "semanticThreshold", true, "Must be a number");
    }

    if (numValue < SEMANTIC_THRESHOLD_MIN || numValue > SEMANTIC_THRESHOLD_MAX) {
        return setFormValues(row, "semanticThreshold", true, `Must be between ${SEMANTIC_THRESHOLD_MIN} and ${SEMANTIC_THRESHOLD_MAX}`);
    }

    return setFormValues(row, "semanticThreshold", false, "");
};

/**
 * Criteria:
 * 1) Required field
 * 2) Must be a positive integer
 * 3) Must be at least SEMANTIC_MAX_RESULTS_PER_RESOURCE_MIN
 * 4) Must be at most SEMANTIC_MAX_RESULTS_PER_RESOURCE_MAX
 */
const semanticMaxResultsPerResourceValidation = (prevConfig, row, value) => {
    // 1)
    if (value === "") {
        return setFormValues(row, "semanticMaxResultsPerResource", true, "Required");
    }

    // 2)
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue !== parseFloat(value)) {
        return setFormValues(row, "semanticMaxResultsPerResource", true, "Must be a whole number");
    }

    // 3)
    if (numValue < SEMANTIC_MAX_RESULTS_PER_RESOURCE_MIN) {
        return setFormValues(row, "semanticMaxResultsPerResource", true, `Must be at least ${SEMANTIC_MAX_RESULTS_PER_RESOURCE_MIN}`);
    }

    // 4)
    if (numValue > SEMANTIC_MAX_RESULTS_PER_RESOURCE_MAX) {
        return setFormValues(row, "semanticMaxResultsPerResource", true, `Must be at most ${SEMANTIC_MAX_RESULTS_PER_RESOURCE_MAX}`);
    }

    return setFormValues(row, "semanticMaxResultsPerResource", false, "");
};

/**
 * Criteria:
 * 1) Required field
 * 2) Must be a non-negative number
 * 3) Must be at most SEMANTIC_MIN_DURATION_MAX
 */
const semanticMinDurationBetweenResultsValidation = (prevConfig, row, value) => {
    // 1)
    if (value === "") {
        return setFormValues(row, "semanticMinDurationBetweenResults", true, "Required");
    }

    // 2)
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
        return setFormValues(row, "semanticMinDurationBetweenResults", true, "Must be a number");
    }

    if (numValue < SEMANTIC_MIN_DURATION_MIN) {
        return setFormValues(row, "semanticMinDurationBetweenResults", true, `Must be at least ${SEMANTIC_MIN_DURATION_MIN}`);
    }

    // 3)
    if (numValue > SEMANTIC_MIN_DURATION_MAX) {
        return setFormValues(row, "semanticMinDurationBetweenResults", true, `Must be at most ${SEMANTIC_MIN_DURATION_MAX} seconds`);
    }

    return setFormValues(row, "semanticMinDurationBetweenResults", false, "");
};

// The main constant, directing to the specific validation function.
export const SEARCH_SETTINGS_VALIDATION = {
    keywordLimit: {
        check: keywordLimitValidation
    },
    keywordTable: {
        check: keywordTableValidation
    },
    keywordThreshold: {
        check: keywordThresholdValidation
    },

    semanticLimit: {
        check: semanticLimitValidation
    },
    semanticThreshold: {
        check: semanticThresholdValidation
    },
    semanticMaxResultsPerResource: {
        check: semanticMaxResultsPerResourceValidation
    },
    semanticMinDurationBetweenResults: {
        check: semanticMinDurationBetweenResultsValidation
    }
};

export const IS_SEARCH_SETTINGS_VALID = (obj) => {
    return !formHasErrors(obj);
};
